import * as React from 'react';
import styled from 'styled-components';
import { Button } from '../../../components/ui/Button';
import { Icon } from '../../../components/ui/Icon';
import { useGenerateReport } from '../hooks/useGenerateReport';
import type { TriggerReportArgs } from '../hooks/useGenerateReport';
import type {
  MarketingReportTimeRange,
  ReportGenerationRequestPayload,
} from '../types';
import type { GenerateReportStatus } from '../types';
import { getReportCopy } from '../lib/reportCopy';
import { ReportPreflightDialog } from './ReportPreflightDialog';

const LiveRegion = styled.div`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
`;

export interface JobUpdateState {
  status: GenerateReportStatus;
  jobId: string | null;
  downloadUrl: string | null;
  expiresAt: string | null;
  error: Error | null;
}

export interface GenerateReportButtonProps {
  restaurantId?: number | string | null;
  timeRange: MarketingReportTimeRange;
  locale?: string;
  templateId?: string | null;
  includeBenchmarks?: boolean;
  reportType?: string;
  idempotencyKey?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  iconName?: 'file' | 'download' | 'chart';
  preflightSections?: readonly string[];
  preflightWarnings?: readonly string[];
  onGenerated?: (result: {
    jobId: string;
    downloadUrl: string;
    expiresAt: string | null;
    reportType: string | null;
    startedAt: string | null;
  }) => void;
  onFailed?: (
    error: Error,
    context: { jobId: string | null; reportType: string | null },
  ) => void;
  onJobUpdate?: (state: JobUpdateState) => void;
  children?: React.ReactNode;
}

function buildPayload({
  timeRange,
  locale,
  includeBenchmarks,
  templateId,
}: {
  timeRange: MarketingReportTimeRange;
  locale?: string;
  includeBenchmarks?: boolean;
  templateId?: string | null;
}): ReportGenerationRequestPayload {
  const payload: ReportGenerationRequestPayload = {
    timeRange,
  };

  if (locale) {
    payload.locale = locale;
  }
  if (includeBenchmarks !== undefined) {
    payload.includeBenchmarks = includeBenchmarks;
  }
  if (templateId !== undefined) {
    payload.templateId = templateId;
  }

  return payload;
}

export const GenerateReportButton = React.forwardRef<
  HTMLButtonElement,
  GenerateReportButtonProps
>((props, ref) => {
  const {
    restaurantId,
    timeRange,
    locale = 'es-ES',
    templateId,
    includeBenchmarks = false,
    reportType,
    idempotencyKey,
    disabled,
    className,
    label,
    iconName = 'file',
    preflightSections,
    preflightWarnings,
    onGenerated,
    onFailed,
    onJobUpdate,
    children,
  } = props;

  const canGenerate = Boolean(restaurantId);
  const [isPreflightOpen, setIsPreflightOpen] = React.useState(false);
  const reportCopy = React.useMemo(() => getReportCopy(locale), [locale]);
  const payloadRef = React.useRef<ReportGenerationRequestPayload>(
    buildPayload({
      timeRange,
      locale,
      includeBenchmarks,
      ...(templateId !== undefined ? { templateId } : {}),
    }),
  );

  React.useEffect(() => {
    payloadRef.current = buildPayload({
      timeRange,
      locale,
      includeBenchmarks,
      ...(templateId !== undefined ? { templateId } : {}),
    });
  }, [timeRange, locale, includeBenchmarks, templateId]);

  const handleGenerated = React.useCallback(
    (result: {
      jobId: string;
      downloadUrl: string;
      expiresAt: string | null;
      reportType: string | null;
      startedAt: string | null;
    }) => {
      onGenerated?.(result);
    },
    [onGenerated],
  );

  const handleFailed = React.useCallback(
    (
      error: Error,
      context: { jobId: string | null; reportType: string | null },
    ) => {
      onFailed?.(error, context);
    },
    [onFailed],
  );

  const {
    status,
    isLoading,
    announcement,
    triggerReport,
    jobId,
    downloadUrl,
    expiresAt,
    error,
  } = useGenerateReport({
    restaurantId: restaurantId ?? null,
    locale,
    onGenerated: handleGenerated,
    onFailed: handleFailed,
  });

  React.useEffect(() => {
    onJobUpdate?.({ status, jobId, downloadUrl, expiresAt, error });
  }, [onJobUpdate, status, jobId, downloadUrl, expiresAt, error]);

  const handleGenerate = React.useCallback(async () => {
    if (!restaurantId || isLoading) {
      return;
    }

    const triggerArgs: TriggerReportArgs = {
      payload: payloadRef.current,
      ...(idempotencyKey ? { idempotencyKey } : {}),
      ...(reportType ? { reportType } : {}),
    };

    try {
      await triggerReport(triggerArgs);
    } catch {
      // The hook owns failure state, announcements, analytics, and callbacks.
    }
  }, [idempotencyKey, isLoading, reportType, restaurantId, triggerReport]);

  const handleClick = React.useCallback(() => {
    if (!restaurantId || isLoading) return;
    setIsPreflightOpen(true);
  }, [isLoading, restaurantId]);

  const handleClosePreflight = React.useCallback(() => {
    if (!isLoading) setIsPreflightOpen(false);
  }, [isLoading]);

  const handleConfirm = React.useCallback(() => {
    setIsPreflightOpen(false);
    void handleGenerate();
  }, [handleGenerate]);

  const buttonLabel = React.useMemo(() => {
    if (typeof children === 'string' || React.isValidElement(children)) {
      return children;
    }
    return label ?? reportCopy.button.generate;
  }, [children, label, reportCopy.button.generate]);

  const isDisabled = disabled || !canGenerate || isLoading;

  return (
    <>
      <Button
        ref={ref}
        className={className}
        variant="primary"
        size="md"
        loading={isLoading}
        disabled={isDisabled}
        onClick={handleClick}
        type="button"
        aria-live="off"
        aria-busy={isLoading}
        aria-haspopup="dialog"
        aria-expanded={isPreflightOpen}
        startIcon={<Icon name={iconName} aria-hidden />}
        aria-disabled={isDisabled}
      >
        {isLoading ? reportCopy.button.generating : buttonLabel}
      </Button>
      <LiveRegion role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </LiveRegion>
      <ReportPreflightDialog
        isOpen={isPreflightOpen}
        timeRange={timeRange}
        locale={locale}
        {...(templateId !== undefined ? { templateId } : {})}
        {...(preflightSections !== undefined
          ? { sections: preflightSections }
          : {})}
        {...(preflightWarnings !== undefined
          ? { warnings: preflightWarnings }
          : {})}
        isGenerating={isLoading}
        onClose={handleClosePreflight}
        onConfirm={handleConfirm}
      />
    </>
  );
});

GenerateReportButton.displayName = 'GenerateReportButton';
